import React from 'react';
import { useCareTypeConfigContext } from '../../../../Components/CareTypeConfigContext';
import { MantraAdminOnly, OzOnly } from '../../../../Components/Permissions';
import { Warning } from '../../../../Components/Warning';
import { CareStatus, PaymentSource } from '../../../../graphQL';
import { PatientCareFlowSectionProps } from './types';
import { Text } from '../../../../globalStyles';
import { InputRHF, SelectRHF } from '../../../../Components/Form';
import {
  LABEL_SIZE,
  NONE_OPTION_ID,
  paymentSourceOptions,
  providersBlockedReasonToLabel,
  REQUIRED_MESSAGE,
} from './utils';
import { usePatientCareFlowSection } from './hooks/logic/usePatientCareFlowSection';

export const PatientCareFlowSection = React.memo(
  ({
    careType,
    providerTitle,
    hasSessionLimit,
    providers,
    validateCareTypes,
    providersBlockedReason,
  }: PatientCareFlowSectionProps): JSX.Element => {
    const { getCareTypeDisplay } = useCareTypeConfigContext();
    const {
      filteredCareStatusOptions,
      careStatusSelectRules,
      providerOptions,
      careStatus,
      paymentSource,
      disabledCareStatus,
    } = usePatientCareFlowSection({
      careType,
      providers,
      validateCareTypes,
    });

    return (
      <div className="w-100">
        <Text.h2 className="mt0 mb3">{getCareTypeDisplay(careType).serviceTitle}</Text.h2>
        <SelectRHF
          name={`${careType}.careStatus`}
          controlProps={{
            label: `${getCareTypeDisplay(careType).serviceTitle} Status`,
            labelSize: LABEL_SIZE,
          }}
          options={filteredCareStatusOptions}
          rules={careStatusSelectRules}
          disabled={disabledCareStatus}
        />
        <OzOnly>
          {careStatus === CareStatus.Active && (
            <>
              <SelectRHF
                controlProps={{ labelSize: LABEL_SIZE, className: 'mb4' }}
                name={`${careType}.paymentSource`}
                rules={{ required: REQUIRED_MESSAGE }}
                options={paymentSourceOptions}
              />
              <MantraAdminOnly>
                {hasSessionLimit && paymentSource === PaymentSource.Organization && (
                  <InputRHF
                    type="number"
                    min={0}
                    name={`${careType}.remainingSessions`}
                    controlProps={{
                      label: `Number ${
                        getCareTypeDisplay(careType).serviceTitle
                      } Sessions Remaining`,
                      labelSize: LABEL_SIZE,
                      className: 'mb4',
                    }}
                  />
                )}
                {!providerOptions.length && providersBlockedReason && (
                  <Warning>{providersBlockedReasonToLabel[providersBlockedReason]}</Warning>
                )}
                <SelectRHF
                  controlProps={{ label: providerTitle, labelSize: LABEL_SIZE, className: 'mb4' }}
                  name={`${careType}.providerId`}
                  options={[{ id: NONE_OPTION_ID, label: 'None' }, ...providerOptions]}
                  disabled={Boolean(!providerOptions.length && providersBlockedReason)}
                />
              </MantraAdminOnly>
            </>
          )}
        </OzOnly>
      </div>
    );
  }
);

// React.memo wraps an anonymous arrow function — set displayName so DevTools
// and error traces show "PatientCareFlowSection" instead of "Memo".
PatientCareFlowSection.displayName = 'PatientCareFlowSection';
